/**
 * Conferma dell'indirizzo dal link ricevuto per email.
 *
 * Ci si arriva cliccando, quindi deve funzionare da sola: nessun modulo da
 * compilare, nessun accesso richiesto. Al massimo si dice cosa fare se il link
 * è scaduto.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { billingApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { Button } from '@/components/ui/button';

export function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [stato, setStato] = useState<'attesa' | 'ok' | 'errore'>('attesa');
  const [messaggio, setMessaggio] = useState('');
  // In sviluppo React monta due volte: senza questa guardia il token verrebbe
  // consumato dalla prima chiamata e la seconda mostrerebbe "già usato".
  const fatto = useRef(false);

  useEffect(() => {
    if (!token || fatto.current) return;
    fatto.current = true;
    billingApi.verifyEmail(token)
      .then((r) => { setStato('ok'); setMessaggio(r.email); })
      .catch((e) => {
        setStato('errore');
        setMessaggio(e instanceof ApiError ? e.message : 'Link non valido o scaduto.');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="bg-background rounded-2xl border shadow-sm p-8 max-w-md w-full text-center">
        {stato === 'attesa' && (
          <>
            <Loader2 className="h-9 w-9 mx-auto animate-spin text-indigo-500" />
            <p className="mt-4 text-sm text-muted-foreground">Un attimo…</p>
          </>
        )}

        {stato === 'ok' && (
          <>
            <CheckCircle2 className="h-11 w-11 mx-auto text-emerald-500" />
            <h1 className="mt-4 text-lg font-bold">Indirizzo confermato</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              <strong>{messaggio}</strong> è tuo. Puoi usare Blagomail senza limitazioni.
            </p>
            <Button className="mt-6 w-full" asChild><Link to="/">Vai alla posta</Link></Button>
          </>
        )}

        {stato === 'errore' && (
          <>
            <XCircle className="h-11 w-11 mx-auto text-amber-500" />
            <h1 className="mt-4 text-lg font-bold">Non ha funzionato</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{messaggio}</p>
            <p className="mt-3 text-sm text-muted-foreground">
              Accedi e richiedi un nuovo messaggio di conferma dalle impostazioni.
            </p>
            <Button variant="outline" className="mt-6 w-full" asChild><Link to="/login">Accedi</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
